import { useEffect } from 'react'
import { Link } from '@tanstack/react-router'
import { Pause, Play, SkipForward } from 'lucide-react'
import { useNow } from '@/hooks/useNow'
import { PHASE_LABEL, phaseMinutes, usePomodoro, type Phase } from '@/state/pomodoro'
import { cx } from './ui'

export const mmss = (ms: number) => {
  const s = Math.max(0, Math.round(ms / 1000))
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`
}

/** Each phase owns a colour so a glance says work or rest without reading. */
export const phaseColour = (p: Phase) =>
  p === 'work' ? 'var(--color-accent)' : p === 'short' ? '#34d399' : '#67e8f9'

/**
 * The clock behind every pomodoro view: a second tick while running, and the
 * end of the phase called from whichever screen is open. Returns what is
 * left, so a view never computes it twice.
 */
export const usePomodoroClock = () => {
  const status = usePomodoro((s) => s.status)
  const deadline = usePomodoro((s) => s.deadline)
  const remaining = usePomodoro((s) => s.remaining)
  const finish = usePomodoro((s) => s.finish)
  const now = useNow(status === 'running' ? 1000 : undefined)
  const left = status === 'running' ? Math.max(0, deadline - now) : remaining
  useEffect(() => {
    if (status === 'running' && deadline <= now) finish()
  }, [status, deadline, now, finish])
  return left
}

/** The dial: a ring that sweeps as the phase burns down, breathing while it runs. */
export const PomodoroDial = ({
  size,
  left,
  className,
}: {
  size: number
  left: number
  className?: string
}) => {
  const phase = usePomodoro((s) => s.phase)
  const status = usePomodoro((s) => s.status)
  const config = usePomodoro((s) => s.config)
  const round = usePomodoro((s) => s.round)
  const total = phaseMinutes(config, phase) * 60_000
  const progress = total ? 1 - left / total : 0
  const stroke = size * 0.055
  const r = size / 2 - stroke
  const circ = 2 * Math.PI * r
  const colour = phaseColour(phase)
  return (
    <div
      className={cx('relative shrink-0', className)}
      style={{ width: size, height: size }}
      data-testid="pomodoro-dial"
      data-phase={phase}
      data-status={status}
    >
      {status === 'running' && (
        <div
          className="dial-halo absolute -inset-3 rounded-full border-2"
          style={{ borderColor: colour }}
          aria-hidden
        />
      )}
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="var(--color-line)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={colour}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={circ * (1 - progress)}
          style={{ transition: 'stroke-dashoffset 900ms linear' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div
          className="font-semibold tabular-nums leading-none"
          style={{ fontSize: Math.round(size * 0.2) }}
          data-testid="pomodoro-time"
        >
          {mmss(left)}
        </div>
        <div className="text-muted mt-2" style={{ fontSize: Math.max(11, Math.round(size * 0.055)) }}>
          {status === 'idle' && phase === 'work' && round === 1 ? 'Ready' : PHASE_LABEL[phase]}
        </div>
        <div className="flex gap-1.5 mt-2" aria-label={`Round ${round} of ${config.rounds}`}>
          {Array.from({ length: config.rounds }, (_, i) => (
            <span
              key={i}
              className="rounded-full"
              style={{
                width: Math.max(6, size * 0.03),
                height: Math.max(6, size * 0.03),
                background:
                  i < round - (phase === 'work' && status === 'idle' ? 1 : 0) ||
                  (phase !== 'work' && i < round)
                    ? colour
                    : 'var(--color-line-strong)',
              }}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * Pinned on every page while a pomodoro is going: the phase, what is left,
 * pause and skip. Tapping it opens Focus. Quiet when nothing is running.
 */
export const PomodoroBar = () => {
  const status = usePomodoro((s) => s.status)
  const phase = usePomodoro((s) => s.phase)
  const title = usePomodoro((s) => s.title)
  const round = usePomodoro((s) => s.round)
  const rounds = usePomodoro((s) => s.config.rounds)
  const pause = usePomodoro((s) => s.pause)
  const resume = usePomodoro((s) => s.resume)
  const skip = usePomodoro((s) => s.skip)
  const left = usePomodoroClock()
  if (status === 'idle') return null
  const colour = phaseColour(phase)
  return (
    <div
      className="glass-bar sticky top-0 z-30 bg-panel/95 backdrop-blur-md border-b border-line px-3 py-1.5 flex items-center gap-3"
      style={{ boxShadow: `inset 0 -2px 0 ${colour}` }}
      data-testid="pomodoro-bar"
    >
      <Link to="/focus" className="flex items-center gap-3 min-w-0 flex-1">
        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: colour }} aria-hidden />
        <span className="text-sm tabular-nums font-semibold">{mmss(left)}</span>
        <span className="text-xs text-muted truncate min-w-0">
          {/* phrases set apart, not joined with dots */}
          <span className="mr-3">{PHASE_LABEL[phase]}</span>
          <span className="mr-3">
            round {round} of {rounds}
          </span>
          {status === 'paused' && <span className="mr-3">paused</span>}
          {title && <span>{title}</span>}
        </span>
      </Link>
      <button
        type="button"
        onClick={status === 'running' ? pause : resume}
        aria-label={status === 'running' ? 'Pause' : 'Resume'}
        className="min-h-9 min-w-9 rounded-[10px] inline-flex items-center justify-center text-fg hover:bg-raised"
        data-testid="pomodoro-bar-toggle"
      >
        {status === 'running' ? <Pause size={16} /> : <Play size={16} />}
      </button>
      <button
        type="button"
        onClick={skip}
        aria-label="Skip phase"
        className="min-h-9 min-w-9 rounded-[10px] inline-flex items-center justify-center text-muted hover:bg-raised"
        data-testid="pomodoro-bar-skip"
      >
        <SkipForward size={16} />
      </button>
    </div>
  )
}
